/**
 * VPR — PUBLIC vendor form (Phase 2). Route: /vendor-form/:token
 *
 * IMPORTANT: register this route OUTSIDE the app's auth guard (like /login) —
 * vendors have no QRAM account. Deliberately self-contained: it does NOT
 * import the authed api client, so the public surface stays decoupled from
 * session handling. Talks only to /api/v1/public/vendor-readiness/*.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiUrl } from '../api/client';

interface FormQuestion {
  qid: string; section: string; question: string; guidance: string; responseType: string;
  /** Answered by choosing one of these instead of Yes/No (the supplier's cryptographic role). */
  options?: string[];
}
interface FormData {
  vendorName: string | null;
  productInScope: string | null;
  catalogVersion: string;
  questions: FormQuestion[];
  responseOptions: string[];
}
interface AnswerDraft { response: string | null; details: string }

// apiUrl() so the public form works under a BASE_PATH deployment too.
const API = apiUrl('/public/vendor-readiness');

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API}${path}`);
  const body = (await res.json().catch(() => null)) as { data?: T; error?: { message?: string } } | null;
  if (!res.ok) throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  return body!.data as T;
}

export function VendorFormPage() {
  const { token } = useParams<{ token: string }>();
  const [form, setForm] = useState<FormData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, AnswerDraft>>({});
  const [signatory, setSignatory] = useState('');
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    getJson<FormData>(`/form/${token}`)
      .then(setForm)
      .catch((e: Error) => setLoadError(e.message));
  }, [token]);

  const sections = useMemo(() => {
    if (!form) return [];
    const bySection = new Map<string, FormQuestion[]>();
    for (const q of form.questions) {
      if (!bySection.has(q.section)) bySection.set(q.section, []);
      bySection.get(q.section)!.push(q);
    }
    return [...bySection.entries()];
  }, [form]);

  const answeredCount = form
    ? form.questions.filter(q => answers[q.qid]?.response).length
    : 0;

  const setAnswer = (qid: string, patch: Partial<AnswerDraft>) =>
    setAnswers(prev => ({ ...prev, [qid]: { ...(prev[qid] ?? { response: null, details: '' }), ...patch } }));

  const submit = async () => {
    if (!form || !token) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API}/form/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers: form.questions.map(q => ({
            qid: q.qid,
            response: q.options ? null : answers[q.qid]?.response ?? null,
            ...(q.options ? { choice: answers[q.qid]?.response ?? null } : {}),
            details: answers[q.qid]?.details?.trim() ? answers[q.qid].details.trim() : null,
          })),
          declaration: {
            signatoryName: signatory.trim(),
            title: title.trim() || null,
            date: new Date().toISOString().slice(0, 10),
          },
        }),
      });
      const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? `Submission failed (${res.status})`);
      setDone(true);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="page vendor-form">
        <h1>Vendor PQC Readiness Questionnaire</h1>
        <p className="error">{loadError}</p>
        <p className="muted">If you believe this link should work, please contact the institution that sent it to you.</p>
      </div>
    );
  }
  if (!form) return <div className="page vendor-form"><p>Loading…</p></div>;
  if (done) {
    return (
      <div className="page vendor-form">
        <h1>Thank you</h1>
        <p>Your response has been submitted successfully. This link is now closed; if corrections
          are needed, the issuing institution will send you a new link.</p>
      </div>
    );
  }

  return (
    <div className="page vendor-form">
      <h1>Vendor PQC Readiness Questionnaire</h1>
      <p className="muted">
        Question Set v{form.catalogVersion} · AvinyaSQ Technologies
        {form.vendorName ? <> · Prepared for <strong>{form.vendorName}</strong></> : null}
        {form.productInScope ? <> · Scope: {form.productInScope}</> : null}
      </p>
      <p>
        For each question select a response and add brief details. {form.questions.length} questions —
        typically under an hour with existing documentation. Your answers are
        submitted once; the link closes on submission.
      </p>
      <p><strong>{answeredCount} of {form.questions.length}</strong> answered</p>

      {sections.map(([section, qs]) => (
        <section key={section} className="card">
          <h2>{section}</h2>
          {qs.map(q => (
            <div key={q.qid} className="form-question">
              <p><strong>{q.qid}.</strong> {q.question}</p>
              <p className="muted small">{q.guidance}</p>
              <label>Response
                <select
                  value={answers[q.qid]?.response ?? ''}
                  onChange={e => setAnswer(q.qid, { response: e.target.value || null })}
                >
                  <option value="">— select —</option>
                  {(q.options ?? form.responseOptions).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
              <label>Details
                <textarea
                  rows={2}
                  maxLength={8000}
                  value={answers[q.qid]?.details ?? ''}
                  onChange={e => setAnswer(q.qid, { details: e.target.value })}
                  placeholder="Standards, versions, dates, document references…"
                />
              </label>
            </div>
          ))}
        </section>
      ))}

      <section className="card">
        <h2>Declaration</h2>
        <p className="small">
          I confirm that the responses provided are accurate to the best of my knowledge
          as of today's date, that referenced evidence is genuine and current, and that
          material roadmap changes will be notified to the issuing institution.
        </p>
        <label>Authorised signatory name
          <input value={signatory} onChange={e => setSignatory(e.target.value)} maxLength={255} />
        </label>
        <label>Title
          <input value={title} onChange={e => setTitle(e.target.value)} maxLength={255} />
        </label>
        {submitError && <p className="error">{submitError}</p>}
        <button disabled={!signatory.trim() || submitting} onClick={() => void submit()}>
          {submitting ? 'Submitting…' : 'Submit response'}
        </button>
        {answeredCount < form.questions.length && (
          <p className="muted small">
            {form.questions.length - answeredCount} question(s) unanswered — you can still
            submit; unanswered items will be flagged to the assessor.
          </p>
        )}
      </section>
    </div>
  );
}
